/**
 * ARC Connection Status Indicator
 * 
 * Shows WebSocket connection status and event counter in the header/navbar
 */

import { Radio, Wifi, WifiOff, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useArcConnectionStatus } from './ArcRealtimeProvider';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface ArcConnectionStatusProps {
  className?: string;
  showEventCount?: boolean;
}

export function ArcConnectionStatus({ 
  className,
  showEventCount = true,
}: ArcConnectionStatusProps) {
  const { wsConnected, wsState, wsEventCount, pendingCount } = useArcConnectionStatus();

  const getStatusConfig = () => {
    switch (wsState) {
      case 'connected':
        return {
          icon: Radio,
          color: 'text-green-500',
          bgColor: 'bg-green-500/10',
          pulse: true,
          label: 'Conectado',
          description: 'Recibiendo eventos en tiempo real',
        };
      case 'connecting':
        return {
          icon: Wifi,
          color: 'text-yellow-500',
          bgColor: 'bg-yellow-500/10',
          pulse: true,
          label: 'Conectando...',
          description: 'Estableciendo conexión con el gateway',
        };
      case 'error':
        return {
          icon: AlertCircle,
          color: 'text-red-500',
          bgColor: 'bg-red-500/10',
          pulse: false,
          label: 'Error',
          description: 'Error de conexión con el gateway',
        };
      case 'disconnected':
      default:
        return {
          icon: WifiOff,
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          pulse: false,
          label: 'Desconectado',
          description: 'Sin conexión al gateway',
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to="/arc/events"
            className={cn(
              'flex items-center gap-2 px-3 py-1.5 rounded-full transition-opacity hover:opacity-80',
              config.bgColor,
              className
            )}
          >
            <div className="relative">
              <Icon className={cn('h-4 w-4', config.color)} />
              {config.pulse && (
                <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                  <span className={cn(
                    'animate-ping absolute inline-flex h-full w-full rounded-full opacity-75',
                    wsConnected ? 'bg-green-400' : 'bg-yellow-400'
                  )} />
                  <span className={cn(
                    'relative inline-flex rounded-full h-2 w-2',
                    wsConnected ? 'bg-green-500' : 'bg-yellow-500'
                  )} />
                </span>
              )}
            </div>
            
            <span className={cn('text-sm font-medium', config.color)}>
              ARC
            </span>

            {showEventCount && pendingCount !== null && pendingCount > 0 && (
              <span className="text-xs text-muted-foreground">
                ({pendingCount})
              </span>
            )}
          </Link>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <div className="text-sm">
            <p className="font-medium">{config.label}</p>
            <p className="text-muted-foreground text-xs">{config.description}</p>
            {pendingCount !== null && pendingCount > 0 && (
              <p className="text-xs mt-1">
                {pendingCount} eventos pendientes — clic para ir al Centro ARC
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
